import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Printer, Copy, Download } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);

export default function PublicStatement() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/statement/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Statement not found or link expired."));
  }, [slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="public-statement-error">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading statement…</div>;

  const { contact, billing_profile, currency, ledger, opening_balance, closing_balance, totals, aging } = data;
  const ccy = currency || "SGD";
  const accent = billing_profile?.accent_color || "#7C3AED";
  const logoUri = billing_profile?.logo_data_uri || null;
  const outstanding = totals?.balance || 0;

  return (
    <div className="min-h-screen bg-white text-black" data-testid="public-statement">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 15mm 12mm; size: A4; }
          body { background: white !important; }
        }
        .stmt-page { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1a1a1a; }
      `}</style>

      <div className="h-1.5 w-full" style={{ background: accent }} data-testid="stmt-accent-ribbon" />

      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="text-sm">
          <span className="font-bold">Statement of Accounts</span> · {contact?.name || contact?.email || "Customer"}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => { navigator.clipboard.writeText(window.location.href).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed")); }}
            data-testid="public-statement-copy-url"
          >
            <Copy className="h-4 w-4 mr-2" />Copy link
          </Button>
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => window.open(`${BACKEND}/api/public/statement/${slug}/pdf`, "_blank")}
            data-testid="public-statement-download-pdf"
          >
            <Download className="h-4 w-4 mr-2" />PDF
          </Button>
          <Button
            className="rounded-sm text-white"
            style={{ backgroundColor: accent }}
            onClick={() => window.print()}
            data-testid="public-statement-print"
          >
            <Printer className="h-4 w-4 mr-2" />Print
          </Button>
        </div>
      </div>

      <div className="stmt-page max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between pb-6 border-b-2" style={{ borderColor: accent }}>
          <div className="flex items-start gap-4">
            {logoUri && (
              <img
                src={logoUri}
                alt="Company logo"
                className="h-16 w-16 object-contain rounded-sm bg-white border border-slate-200 p-1.5 shrink-0"
                data-testid="statement-logo"
              />
            )}
            <div>
              {(billing_profile?.company_name || billing_profile?.division_name) && (
                <div className="text-2xl font-black tracking-tight" data-testid="statement-company-name">{billing_profile?.company_name || billing_profile?.division_name}</div>
              )}
              {billing_profile?.company_name && billing_profile?.division_name && billing_profile.company_name !== billing_profile.division_name && (
                <div className="text-xs text-slate-600 font-semibold mt-0.5" data-testid="statement-division-name">{billing_profile.division_name}</div>
              )}
              {billing_profile?.uen && <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">UEN {billing_profile.uen}</div>}
              {billing_profile?.address && <div className="text-xs text-slate-500 mt-1">{billing_profile.address}{billing_profile?.postal_code ? ` ${billing_profile.postal_code}` : ""}</div>}
              <div className="text-xs text-slate-500 mt-1">
                {[billing_profile?.email, billing_profile?.phone, billing_profile?.website].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: accent }}>Statement of Accounts</div>
            <h1 className="text-3xl font-black mt-1">{contact?.name || "—"}</h1>
            {contact?.email && <div className="text-xs text-slate-600 mt-1">{contact.email}</div>}
            <div className="text-xs text-slate-500 mt-1">As of {new Date().toISOString().slice(0, 10)}</div>
          </div>
        </div>

        {/* Outstanding hero */}
        <div
          className="mt-8 rounded-sm p-6 flex items-center justify-between"
          style={{ background: `${accent}0D`, border: `1.5px solid ${accent}` }}
          data-testid="stmt-hero"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">Total outstanding</div>
            <div className="text-4xl font-black mt-2 tracking-tight" style={{ color: outstanding > 0.005 ? accent : "#059669" }}>
              {fmt(outstanding, ccy)}
            </div>
          </div>
          <div className="text-right text-xs text-slate-600">
            <div>Invoiced: <span className="font-semibold text-slate-900">{fmt(totals?.invoiced, ccy)}</span></div>
            <div className="mt-1">Paid: <span className="font-semibold text-emerald-600">{fmt(totals?.paid, ccy)}</span></div>
            <div className="mt-1">Invoices: <span className="font-semibold text-slate-900">{totals?.invoice_count || 0}</span></div>
          </div>
        </div>

        {/* Ledger */}
        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">Ledger</div>
          <div className="border border-slate-200 rounded-sm" data-testid="stmt-ledger">
            <div className="grid grid-cols-[100px_110px_1fr_110px_110px_120px] gap-2 px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              <div>Date</div><div>Ref</div><div>Description</div>
              <div className="text-right">Debit</div><div className="text-right">Credit</div><div className="text-right">Balance</div>
            </div>
            <div className="grid grid-cols-[100px_110px_1fr_110px_110px_120px] gap-2 px-3 py-2 border-b border-slate-100 text-xs italic text-slate-500">
              <div></div><div></div><div>Opening balance</div>
              <div></div><div></div><div className="text-right">{fmt(opening_balance, ccy)}</div>
            </div>
            {ledger?.length === 0 && (
              <div className="px-3 py-8 text-center text-xs text-slate-500">No transactions.</div>
            )}
            {(ledger || []).map((row, i) => (
              <div key={i} className="grid grid-cols-[100px_110px_1fr_110px_110px_120px] gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 text-xs items-center">
                <div className="text-slate-600">{row.date || "—"}</div>
                <div className="font-mono text-[10px]">{row.ref}</div>
                <div className="truncate" title={row.description}>{row.description}</div>
                <div className={`text-right ${row.debit ? "text-slate-900" : "text-slate-300"}`}>{row.debit ? fmt(row.debit, ccy) : "—"}</div>
                <div className={`text-right ${row.credit ? "text-emerald-600 font-semibold" : "text-slate-300"}`}>{row.credit ? fmt(row.credit, ccy) : "—"}</div>
                <div className="text-right font-semibold">{fmt(row.balance_after, ccy)}</div>
              </div>
            ))}
            <div className="grid grid-cols-[100px_110px_1fr_110px_110px_120px] gap-2 px-3 py-2 border-t-2 border-slate-300 bg-slate-50 text-xs font-bold">
              <div></div><div></div><div>Closing balance</div>
              <div></div><div></div><div className="text-right">{fmt(closing_balance, ccy)}</div>
            </div>
          </div>
        </div>

        {/* Aging */}
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold mb-2">Outstanding aging</div>
          <div className="grid grid-cols-5 gap-2" data-testid="stmt-aging">
            <AgingCell label="Current" value={fmt(aging?.current, ccy)} />
            <AgingCell label="1-30" value={fmt(aging?.d1_30, ccy)} />
            <AgingCell label="31-60" value={fmt(aging?.d31_60, ccy)} />
            <AgingCell label="61-90" value={fmt(aging?.d61_90, ccy)} />
            <AgingCell label="90+" value={fmt(aging?.d90_plus, ccy)} tone="warn" />
          </div>
        </div>

        <div className="mt-8 text-[11px] text-slate-500 text-center">
          For questions about this statement, please contact us at {billing_profile?.email || billing_profile?.phone || "the company"}.
        </div>
      </div>
    </div>
  );
}

function AgingCell({ label, value, tone = "neutral" }) {
  const cls = tone === "warn" ? "text-amber-600 bg-amber-50" : "text-slate-900 bg-slate-50";
  return (
    <div className={`rounded-sm border border-slate-200 p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-70">{label}</div>
      <div className="text-lg font-black mt-1 tracking-tight">{value}</div>
    </div>
  );
}
